'use client';

import Icon from '@/components/ui/AppIcon';

/**
 * Render an embedded live stream for a competition draw. Accepts a
 * Facebook video URL, a YouTube live URL, or a generic embed URL and
 * picks the right player.
 *
 * Renders nothing if no URL is set.
 */

function classifyAndBuild(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  // YouTube — both watch?v= and youtu.be/ short links.
  const yt = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/i);
  if (yt) {
    const id = yt[1];
    return {
      kind: 'youtube',
      src: `https://www.youtube.com/embed/${id}?autoplay=0&modestbranding=1&rel=0`,
      allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    };
  }

  // Facebook video / live.
  if (/facebook\.com\//i.test(trimmed)) {
    const encoded = encodeURIComponent(trimmed);
    return {
      kind: 'facebook',
      src: `https://www.facebook.com/plugins/video.php?href=${encoded}&show_text=false&autoplay=false`,
      allow: 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share'
    };
  }

  // Vimeo player.com / vimeo.com short link
  const vm = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vm) {
    return {
      kind: 'vimeo',
      src: `https://player.vimeo.com/video/${vm[1]}`,
      allow: 'autoplay; fullscreen; picture-in-picture'
    };
  }

  // Fallback: if it already looks like an embed URL, just use it directly.
  if (/\/(embed|player)\//i.test(trimmed)) {
    return { kind: 'generic', src: trimmed, allow: 'autoplay; fullscreen; picture-in-picture' };
  }

  return null;
}

export default function LiveDrawEmbed({ url, title = 'Live draw', className = '' }) {
  const player = classifyAndBuild(url);
  if (!player) return null;

  return (
    <div className={`bg-card border border-border rounded-standard overflow-hidden shadow-soft ${className}`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-accent/5">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
        </span>
        <Icon name="VideoCameraIcon" size={16} className="text-accent" />
        <h3 className="text-sm font-semibold text-card-foreground">Watch the live draw</h3>
      </div>
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={player.src}
          title={title}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          scrolling="no"
          allow={player.allow}
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
  );
}
